import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiClock, FiSave, FiArrowRight, FiArrowLeft, FiCheckCircle, FiLogOut } from "react-icons/fi";
import getApiBaseUrl from "../../utils/api";

import MAT_CONFIG from "./MatConfig";
import matLanguageComprehension from "./MatLanguageComprehension";
import matIntelligenceReasoning from "./MatIntelligenceReasoning";
import matDataAnalysis from "./MatDataAnalysis";
import matMathematicalSkills from "./MatMathematicalSkills";
import matEconomicBusiness from "./MatEconomicBusiness";
import { calculateScore, buildBreakdown, calculatePercentile, estimateCompositeScore } from "./MatScoring";

const DATA_MAP = {
  languageComprehension: matLanguageComprehension,
  intelligenceReasoning: matIntelligenceReasoning,
  dataAnalysis: matDataAnalysis,
  mathematicalSkills: matMathematicalSkills,
  economicBusiness: matEconomicBusiness
};

const SECTIONS = MAT_CONFIG.sections.map((s) => ({
  ...s,
  questions: DATA_MAP[s.key] || []
}));

const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.questions);

// CSS selector for the actual site header. Adjust this if your header
// uses a different tag/class/id (e.g. ".site-header", "#main-nav").
const SITE_HEADER_SELECTOR = "header";
// Fallback height (px) used before the real header has been measured,
// or if no matching header element is found on the page.
const FALLBACK_HEADER_HEIGHT = 80;

export default function MatTest() {
  const navigate = useNavigate();

  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState({});
  const [timeLeft, setTimeLeft] = useState(MAT_CONFIG.durationMinutes * 60);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(null);

  const submittedRef = useRef(false);
  const answersRef = useRef({});

  useEffect(() => { answersRef.current = answers; }, [answers]);

  const API_URL = getApiBaseUrl();
  const currentSection = SECTIONS[activeSectionIndex];
  const isLastSection = activeSectionIndex === SECTIONS.length - 1;
  const currentQuestion = currentSection.questions[currentQIndex];
  const isLastQuestionInSection = currentQIndex === currentSection.questions.length - 1;

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (footer) footer.style.display = "none";
    return () => {
      if (footer) footer.style.display = "";
    };
  }, []);
  // Always open the test scrolled to the very top. Without this, the
  // browser can carry over the scroll position from whatever page the
  // user navigated from, landing them mid-passage instead of at Q1.
  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  // Measure the real site header height at runtime and expose it as a CSS
  // custom property so the page always starts flush below it, instead of
  // relying on a hardcoded pixel value that breaks whenever the header's
  // actual height changes (e.g. when it wraps to two lines on narrow
  // screens, or grows/shrinks on scroll).
  useEffect(() => {
    const setHeaderHeightVar = () => {
      const header = document.querySelector(SITE_HEADER_SELECTOR);
      const height = header ? header.getBoundingClientRect().height : FALLBACK_HEADER_HEIGHT;
      document.documentElement.style.setProperty("--site-header-height", `${height}px`);
    };

    setHeaderHeightVar();
    window.addEventListener("resize", setHeaderHeightVar);
    window.addEventListener("orientationchange", setHeaderHeightVar);

    let resizeObserver;
    const headerEl = document.querySelector(SITE_HEADER_SELECTOR);
    if (headerEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(setHeaderHeightVar);
      resizeObserver.observe(headerEl);
    }

    return () => {
      window.removeEventListener("resize", setHeaderHeightVar);
      window.removeEventListener("orientationchange", setHeaderHeightVar);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Measure the real, visible viewport height in JS and expose it as a CSS
  // custom property. `100dvh` covers most modern browsers, but this is a
  // fallback for older mobile browsers/webviews that don't support dvh and
  // would otherwise size the page using 100vh, which on many phones includes
  // space hidden behind the address bar — causing the bottom bar or submit
  // button to sit off-screen until the page is scrolled.
  useEffect(() => {
    const setViewportHeightVar = () => {
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty("--app-vh", `${vh}px`);
    };

    setViewportHeightVar();
    window.addEventListener("resize", setViewportHeightVar);
    window.addEventListener("orientationchange", setViewportHeightVar);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportHeightVar);
    }

    return () => {
      window.removeEventListener("resize", setViewportHeightVar);
      window.removeEventListener("orientationchange", setViewportHeightVar);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setViewportHeightVar);
      }
    };
  }, []);

  useEffect(() => {
    if (showResult) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showResult]);

  // Mark whichever question is currently on screen as "visited" for the palette.
  useEffect(() => {
    if (!currentQuestion) return;
    const qId = currentQuestion.id;
    setVisited((prev) => (prev[qId] ? prev : { ...prev, [qId]: true }));
  }, [currentQuestion]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSelect = (qId, letter) => {
    if (showResult) return;
    setAnswers((prev) => ({ ...prev, [qId]: letter }));
  };

  const isAnswered = (id) => answers[id] !== undefined && answers[id] !== "";

  // Status for the question palette: 'current' | 'answered' | 'notAnswered' | 'notVisited'
  const getQuestionStatus = (idx, q) => {
    if (idx === currentQIndex) return "current";
    if (isAnswered(q.id)) return "answered";
    if (visited[q.id]) return "notAnswered";
    return "notVisited";
  };

  const paletteCounts = currentSection.questions.reduce(
    (acc, q, idx) => {
      const status = getQuestionStatus(idx, q);
      acc[status] += 1;
      return acc;
    },
    { current: 0, answered: 0, notAnswered: 0, notVisited: 0 }
  );

  const totalAnsweredCount = Object.keys(answers).length;

  const getSharedContext = (q) => {
    const groupId = q.passageId || q.setId;
    if (!groupId) {
      return { passage: q.passage || null, statement: q.statement || null, table: q.table || null };
    }
    const source = currentSection.questions.find(
      (sq) => (sq.passageId === groupId || sq.setId === groupId) && (sq.passage || sq.statement || sq.table)
    );
    return {
      passage: q.passage || source?.passage || null,
      statement: q.statement || source?.statement || null,
      table: q.table || source?.table || null
    };
  };

  const goNextQuestion = () => {
    if (currentQIndex < currentSection.questions.length - 1) {
      setCurrentQIndex((i) => i + 1);
      window.scrollTo(0, 0);
    }
  };

  const goPrevQuestion = () => {
    if (currentQIndex > 0) {
      setCurrentQIndex((i) => i - 1);
      window.scrollTo(0, 0);
    }
  };

  const jumpToQuestion = (idx) => {
    setCurrentQIndex(idx);
    window.scrollTo(0, 0);
  };

  const saveTestResult = async (finalScore, percentile, composite, breakdown) => {
    try {
      await fetch(`${API_URL}/tests/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          testName: "MAT Mock Test",
          result: {
            marks: finalScore.marks,
            correct: finalScore.correct,
            wrong: finalScore.wrong,
            unattempted: finalScore.unattempted,
            total: MAT_CONFIG.totalMarks,
            percentile,
            composite,
            breakdown
          }
        })
      });
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const handleSubmit = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    const finalScore = calculateScore(ALL_QUESTIONS, answersRef.current);
    const percentile = calculatePercentile(finalScore.marks);
    const composite = estimateCompositeScore(percentile);
    const breakdown = buildBreakdown(ALL_QUESTIONS, answersRef.current);

    setScore({ ...finalScore, percentile, composite });
    setShowResult(true);
    window.scrollTo(0, 0);
    saveTestResult(finalScore, percentile, composite, breakdown);
  };

  const goNextSection = () => {
    if (activeSectionIndex < SECTIONS.length - 1) {
      setActiveSectionIndex((i) => i + 1);
      setCurrentQIndex(0);
      window.scrollTo(0, 0);
    }
  };

  const goPrevSection = () => {
    if (activeSectionIndex > 0) {
      setActiveSectionIndex((i) => i - 1);
      setCurrentQIndex(0);
      window.scrollTo(0, 0);
    }
  };

  const selectSection = (idx) => {
    setActiveSectionIndex(idx);
    setCurrentQIndex(0);
    window.scrollTo(0, 0);
  };

  const handleManualSubmit = () => {
    const unansweredCount = MAT_CONFIG.totalQuestions - totalAnsweredCount;
    const confirmMsg = unansweredCount > 0
      ? `You have ${unansweredCount} unanswered question(s). Are you sure you want to submit the test now? This cannot be undone.`
      : "Are you sure you want to submit the test now? This cannot be undone.";
    if (window.confirm(confirmMsg)) {
      handleSubmit();
    }
  };

  const handleExitTest = () => {
    const confirmExit = window.confirm(
      "Are you sure you want to exit? Your progress will not be saved and the timer will reset if you start the test again."
    );
    if (confirmExit) {
      navigate("/");
    }
  };

  // ---------- RESULT SCREEN ----------
  if (showResult && score) {
    return (
      <div className="mat-result-container">
        <style>{`
          .mat-result-container {
            min-height: 100vh;
            min-height: var(--app-vh, 100vh);
            background: #ffffff;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            padding: 1.25rem;
            padding-top: calc(var(--site-header-height, 80px) + 40px);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .result-card {
            width: 100%;
            max-width: 680px;
            background: #ffffff;
            border: 1px solid #bbf7d0;
            border-radius: 20px;
            padding: 2.5rem;
            text-align: center;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          }
          .score-row {
            display: flex;
            justify-content: center;
            gap: 1.2rem;
            margin: 1.5rem 0;
            flex-wrap: wrap;
          }
          .score-box {
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            border-radius: 14px;
            padding: 1.1rem 1.5rem;
            min-width: 100px;
            flex: 1 1 100px;
          }
          .score-num { font-size: 1.6rem; font-weight: 800; color: #16a34a; }
          .score-num.negative { color: #dc2626; }
          .score-label { font-size: 0.75rem; color: #6b7280; margin-top: 4px; }
          .composite-box {
            background: linear-gradient(135deg, #16a34a, #22c55e);
            color: white;
            border-radius: 16px;
            padding: 1.6rem;
            margin-top: 1.5rem;
          }
          .composite-num { font-size: 2.4rem; font-weight: 900; word-break: break-word; }
          .composite-label { font-size: 0.8rem; opacity: 0.9; margin-top: 4px; }
          .disclaimer {
            background: #fffbeb;
            border: 1px solid #fde68a;
            color: #92400e;
            font-size: 0.8rem;
            border-radius: 10px;
            padding: 0.7rem 1rem;
            margin-top: 1rem;
          }
          .primary-btn {
            background: #16a34a;
            color: white;
            border: none;
            padding: 0.9rem 2.2rem;
            border-radius: 10px;
            font-weight: 700;
            cursor: pointer;
            margin-top: 1.5rem;
            width: 100%;
            max-width: 280px;
            min-height: 48px;
          }

          @media (max-width: 480px) {
            .mat-result-container { padding: 0.9rem; padding-top: calc(var(--site-header-height, 80px) + 24px); }
            .result-card { padding: 1.6rem 1.2rem; border-radius: 14px; }
            .score-row { gap: 0.7rem; margin: 1.2rem 0; }
            .score-box { padding: 0.85rem 0.9rem; min-width: 78px; }
            .score-num { font-size: 1.25rem; }
            .score-label { font-size: 0.68rem; }
            .composite-box { padding: 1.2rem 1rem; }
            .composite-num { font-size: 1.7rem; }
            .composite-label { font-size: 0.72rem; }
          }

          @media (max-width: 360px) {
            .score-row { gap: 0.5rem; }
            .score-box { min-width: 68px; padding: 0.7rem 0.6rem; flex: 1 1 68px; }
            .score-num { font-size: 1.1rem; }
          }
        `}</style>
        <div className="result-card">
          <FiCheckCircle style={{ fontSize: "3rem", color: "#19fd91" }} />
          <h2 style={{ color: "#166534", marginTop: "0.5rem" }}>MAT Mock Test Complete</h2>

          <div className="score-row">
            <div className="score-box">
              <div className={`score-num ${score.marks < 0 ? "negative" : ""}`}>{score.marks}</div>
              <div className="score-label">Raw Marks (of {MAT_CONFIG.totalMarks})</div>
            </div>
            <div className="score-box">
              <div className="score-num">{score.correct}</div>
              <div className="score-label">Correct</div>
            </div>
            <div className="score-box">
              <div className="score-num negative">{score.wrong}</div>
              <div className="score-label">Wrong</div>
            </div>
            <div className="score-box">
              <div className="score-num" style={{ color: "#6b7280" }}>{score.unattempted}</div>
              <div className="score-label">Unattempted</div>
            </div>
          </div>

          <div className="composite-box">
            <div className="composite-num">{score.composite}/{MAT_CONFIG.compositeScale.max}</div>
            <div className="composite-label">
              Estimated Composite Score · Estimated Percentile: {score.percentile}%
            </div>
          </div>

          <div className="disclaimer">
            Composite score and percentile are estimates only, not an official AIMA score.
            Accuracy improves as more users take this mock test.
          </div>

          <button className="primary-btn" onClick={() => navigate("/profile")}>Go to Profile</button>
        </div>
      </div>
    );
  }

  // ---------- MAIN TEST SCREEN ----------
  const sharedContext = getSharedContext(currentQuestion);

  return (
    <div className="mat-container">
      <style>{`
        * { box-sizing: border-box; }

        .mat-container {
          height: 100vh;
          height: var(--app-vh, 100vh);
          height: 100dvh;
          display: flex;
          flex-direction: column;
          background: #f7fdf9;
          padding: 1.2rem 1.5rem;
          padding-top: calc(var(--site-header-height, 80px) + 14px);
          color: #111827;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow: hidden;
          overscroll-behavior: contain;
        }

        .top-bar {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto 0.7rem auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #d1fae5;
          padding-bottom: 0.6rem;
          flex-wrap: wrap;
          gap: 0.6rem;
          flex-shrink: 0;
        }

        .test-title { font-size: 1.2rem; font-weight: 800; color: #166534; line-height: 1.2; letter-spacing: -0.01em; }
        .section-label { font-size: 0.85rem; color: #6b7280; margin-top: 2px; }

        .timer-box {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'SF Mono', ui-monospace, monospace;
          font-size: 1.05rem;
          font-weight: 800;
          padding: 7px 14px;
          border-radius: 12px;
          border: 1px solid #bbf7d0;
          background: #f0fdf4;
          color: #166534;
          white-space: nowrap;
          flex-shrink: 0;
          transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        }
        .timer-warning { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }

        .section-strip {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto 0.6rem auto;
          display: flex;
          gap: 0.5rem;
          overflow-x: auto;
          padding-bottom: 0.25rem;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x proximity;
          flex-shrink: 0;
        }
        .section-pill {
          flex-shrink: 0;
          padding: 0.4rem 0.9rem;
          border-radius: 999px;
          border: 1px solid #d1fae5;
          background: #ffffff;
          color: #166534;
          font-size: 0.78rem;
          font-weight: 700;
          cursor: pointer;
          min-height: 36px;
          scroll-snap-align: start;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .section-pill:hover:not(.active) { background: #f0fdf4; border-color: #86efac; }
        .section-pill.active { background: #166534; border-color: #166534; color: white; box-shadow: 0 2px 6px rgba(22, 101, 52, 0.25); }

        .main-layout {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 0.8rem;
          align-items: stretch;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
        }

        .question-card {
          background: #ffffff;
          border: 1px solid #d1fae5;
          border-radius: 14px;
          padding: 1.1rem 1.4rem;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04), 0 1px 2px rgba(16, 24, 40, 0.03);
        }

        .q-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.6rem; flex-wrap: wrap; gap: 0.5rem; flex-shrink: 0; }
        .q-position {
          font-size: 0.78rem;
          font-weight: 800;
          color: #15803d;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
        }
        .q-progress { font-size: 0.8rem; color: #6b7280; font-weight: 500; }

        .q-text { color: #14532d; font-size: 1rem; font-weight: 500; line-height: 1.5; white-space: pre-line; margin-bottom: 0.7rem; word-break: break-word; overflow-wrap: break-word; flex-shrink: 0; }

        .rc-passage-panel, .di-statement {
          background: #f9fafb;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 0.7rem;
          font-size: 0.87rem;
          color: #374151;
          line-height: 1.5;
          max-height: 150px;
          overflow-y: auto;
          white-space: pre-line;
          word-break: break-word;
          overflow-wrap: break-word;
          -webkit-overflow-scrolling: touch;
          flex-shrink: 0;
        }

        .di-table-wrap { overflow-x: auto; margin-bottom: 0.7rem; -webkit-overflow-scrolling: touch; flex-shrink: 0; border-radius: 8px; border: 1px solid #d1d5db; }
        .di-table { width: 100%; border-collapse: collapse; min-width: 360px; }
        .di-table th, .di-table td { border: 1px solid #e5e7eb; padding: 0.4rem 0.6rem; font-size: 0.82rem; text-align: left; background: #ffffff; white-space: nowrap; }
        .di-table th { background: #f0fdf4; color: #166534; font-weight: 700; border-top: none; }
        .di-table tr:first-child th { border-top: none; }
        .di-table td { border-bottom: none; }
        .di-table tr:last-child td { border-bottom: none; }

        .options-wrap { display: flex; flex-direction: column; gap: 0.4rem; flex-shrink: 0; }
        .option-row {
          padding: 0.6rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          cursor: pointer;
          font-size: 0.9rem;
          color: #374151;
          background: #ffffff;
          transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
          word-break: break-word;
          overflow-wrap: break-word;
          min-height: 40px;
          display: flex;
          align-items: center;
          -webkit-tap-highlight-color: transparent;
        }
        .option-row:hover { background: #f9fffb; border-color: #bbf7d0; }
        .option-row.selected { border-color: #16a34a; background: #f0fdf4; color: #14532d; font-weight: 600; box-shadow: 0 0 0 1px #16a34a inset; }
        .option-row:active { background: #dcfce7; }

        .q-nav-row { display: flex; justify-content: space-between; align-items: center; margin-top: 0.8rem; gap: 0.6rem; flex-shrink: 0; }
        .q-nav-btn {
          background: #ffffff;
          color: #166534;
          border: 1px solid #bbf7d0;
          padding: 0.5rem 1.1rem;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          font-size: 0.82rem;
          min-height: 40px;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .q-nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .q-nav-btn:hover:not(:disabled) { background: #f0fdf4; }
        /* Highlighted variant used when the "Next" action in the question
           card actually advances to a new section rather than a question,
           so it reads as a more deliberate action than a plain Next click. */
        .q-nav-btn--section {
          background: #16a34a;
          color: #ffffff;
          border-color: #16a34a;
          box-shadow: 0 2px 6px rgba(22, 163, 74, 0.25);
        }
        .q-nav-btn--section:hover:not(:disabled) { background: #15803d; }

        .palette-panel {
          background: #ffffff;
          border: 1px solid #d1fae5;
          border-radius: 14px;
          padding: 0.85rem;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04), 0 1px 2px rgba(16, 24, 40, 0.03);
        }
        .palette-title { font-size: 0.78rem; font-weight: 800; color: #166534; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.5rem; flex-shrink: 0; }
        .palette-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(38px, 1fr));
          gap: 0.32rem;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          flex: 1 1 auto;
          min-height: 0;
        }
        .pal-btn {
          width: 100%;
          aspect-ratio: 1 / 1;
          min-height: 34px;
          border-radius: 9px;
          border: 1px solid #d1d5db;
          background: #f9fafb;
          color: #6b7280;
          font-size: 0.76rem;
          font-weight: 750;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
        }
        .pal-btn:hover { transform: translateY(-1px); }
        .pal-btn.current { border-color: #16a34a; background: #16a34a; color: white; box-shadow: 0 2px 5px rgba(22, 163, 74, 0.3); }
        .pal-btn.answered { background: #dcfce7; border-color: #16a34a; color: #166534; }
        .pal-btn.notAnswered { background: #fee2e2; border-color: #fca5a5; color: #dc2626; }
        .pal-btn.notVisited { background: #f9fafb; border-color: #d1d5db; color: #9ca3af; }

        .palette-legend { margin-top: 0.6rem; display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.7rem; color: #6b7280; flex-shrink: 0; }
        .legend-dot { display: inline-block; width: 9px; height: 9px; border-radius: 3px; margin-right: 6px; vertical-align: middle; flex-shrink: 0; }

        .bottom-bar {
          width: 100%;
          max-width: 1200px;
          margin: 0.6rem auto 0 auto;
          background: #ffffff;
          border: 1px solid #d1fae5;
          border-radius: 14px;
          padding: 0.6rem 1rem;
          flex-shrink: 0;
          box-shadow: 0 -1px 3px rgba(16, 24, 40, 0.03);
        }
        .bottom-bar-inner { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.7rem; }
        .dock-left { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .dock-right { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }

        .btn-primary {
          background: #16a34a;
          color: white;
          border: none;
          padding: 0.55rem 1.3rem;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 40px;
          font-size: 0.88rem;
          box-shadow: 0 2px 6px rgba(22, 163, 74, 0.25);
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .btn-primary:hover { background: #15803d; box-shadow: 0 3px 8px rgba(22, 163, 74, 0.32); }
        .btn-secondary {
          background: #ffffff;
          color: #166534;
          border: 1px solid #bbf7d0;
          padding: 0.55rem 1.1rem;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 40px;
          font-size: 0.88rem;
          transition: background-color 0.15s ease;
        }
        .btn-secondary:hover { background: #f0fdf4; }
        .btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }
        .btn-exit {
          background: #ffffff;
          color: #dc2626;
          border: 1px solid #fca5a5;
          padding: 0.5rem 0.9rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          font-size: 0.8rem;
          min-height: 38px;
          transition: background-color 0.15s ease;
        }
        .btn-exit:hover { background: #fee2e2; }

        /* ---------- RESPONSIVE ---------- */
        /* Below 900px there isn't room for a side palette, so it becomes a
           short horizontal-scroll strip docked under the question card —
           this keeps everything inside one screen height with no page scroll. */
        @media (max-width: 900px) {
          .main-layout {
            grid-template-columns: 1fr;
            grid-template-rows: 1fr auto;
            gap: 0.5rem;
          }
          .palette-panel {
            order: 2;
            flex-direction: row;
            align-items: center;
            gap: 0.6rem;
            padding: 0.5rem 0.7rem;
            max-height: 64px;
          }
          .palette-title { display: none; }
          .palette-grid {
            grid-auto-flow: column;
            grid-template-columns: none;
            grid-template-rows: repeat(2, 1fr);
            grid-auto-columns: 30px;
            overflow-x: auto;
            overflow-y: hidden;
          }
          .pal-btn { min-height: 0; font-size: 0.68rem; }
          .palette-legend { display: none; }
        }

        @media (max-width: 720px) {
          .mat-container { padding: 0.9rem 1rem; padding-top: calc(var(--site-header-height, 80px) + 12px); }
          .question-card { padding: 0.9rem 1.1rem; }
        }

        @media (max-width: 600px) {
          .top-bar { align-items: center; justify-content: space-between; gap: 0.4rem; margin-bottom: 0.5rem; padding-bottom: 0.45rem; }
          .test-title { font-size: 1rem; }
          .section-label { font-size: 0.75rem; }
          .timer-box { font-size: 0.9rem; padding: 5px 10px; }
          .section-strip { margin-bottom: 0.45rem; }
          .q-text { font-size: 0.92rem; margin-bottom: 0.55rem; }
          .q-header { margin-bottom: 0.45rem; }
          .options-wrap { gap: 0.35rem; }
          .option-row { padding: 0.55rem 0.85rem; min-height: 38px; }
          .q-nav-row { margin-top: 0.6rem; }
        }

        @media (max-width: 480px) {
          .mat-container { padding: 0.6rem 0.7rem; padding-top: calc(var(--site-header-height, 80px) + 10px); }
          .question-card { padding: 0.75rem 0.85rem; border-radius: 10px; }
          .rc-passage-panel, .di-statement { padding: 0.6rem 0.75rem; max-height: 110px; margin-bottom: 0.5rem; }
          .option-row { padding: 0.5rem 0.75rem; font-size: 0.87rem; }
          .section-pill { padding: 0.32rem 0.7rem; font-size: 0.7rem; min-height: 32px; }
          .palette-panel { max-height: 54px; padding: 0.4rem 0.55rem; }
          .bottom-bar { padding: 0.5rem 0.7rem; margin-top: 0.5rem; }
          .dock-left { width: 100%; justify-content: space-between; }
          .dock-right { flex-direction: row; flex-wrap: wrap; width: 100%; }
          .btn-secondary, .btn-primary { flex: 1 1 auto; justify-content: center; }
        }

        @media (max-width: 380px) {
          .test-title { font-size: 0.92rem; }
          .section-label { font-size: 0.68rem; }
          .timer-box { font-size: 0.8rem; padding: 5px 9px; gap: 5px; }
          .q-position { font-size: 0.75rem; }
          .q-progress { font-size: 0.68rem; }
          .q-text { font-size: 0.87rem; }
          .option-row { font-size: 0.83rem; padding: 0.45rem 0.65rem; min-height: 36px; }
        }

        @media (max-height: 640px) {
          .mat-container { padding-top: calc(var(--site-header-height, 80px) + 8px); }
          .q-text { margin-bottom: 0.4rem; }
          .options-wrap { gap: 0.3rem; }
          .option-row { min-height: 34px; padding: 0.45rem 0.8rem; }
          .rc-passage-panel, .di-statement { max-height: 90px; }
        }
      `}</style>

      <div className="top-bar">
        <div>
          <div className="test-title">{MAT_CONFIG.title}</div>
          <div className="section-label">
            Section {activeSectionIndex + 1} of {SECTIONS.length}: {currentSection.label} — Question {currentQIndex + 1} of {currentSection.questions.length}
          </div>
        </div>
        <div className={`timer-box ${timeLeft < 300 ? "timer-warning" : ""}`}>
          <FiClock /> {formatTime(timeLeft)}
        </div>
      </div>

      <div className="section-strip">
        {SECTIONS.map((s, idx) => (
          <button
            key={s.key}
            className={`section-pill ${idx === activeSectionIndex ? "active" : ""}`}
            onClick={() => selectSection(idx)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="main-layout">
        <div className="question-card">
          <div className="q-header">
            <span className="q-position">Q{currentQuestion.id}</span>
            <span className="q-progress">Question {currentQIndex + 1} of {currentSection.questions.length}</span>
          </div>

          {sharedContext.passage && <div className="rc-passage-panel">{sharedContext.passage}</div>}
          {sharedContext.statement && <div className="di-statement">{sharedContext.statement}</div>}
          {sharedContext.table && (
            <div className="di-table-wrap">
              <table className="di-table">
                <thead>
                  <tr>{sharedContext.table.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {sharedContext.table.rows.map((row, i) => (
                    <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="q-text">{currentQuestion.question}</div>

          <div className="options-wrap">
            {currentQuestion.options.map((opt) => {
              const letter = opt.trim().charAt(0);
              const isSelected = answers[currentQuestion.id] === letter;
              return (
                <div
                  key={opt}
                  className={`option-row ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(currentQuestion.id, letter)}
                >
                  {opt}
                </div>
              );
            })}
          </div>

          <div className="q-nav-row">
            <button className="q-nav-btn" onClick={goPrevQuestion} disabled={currentQIndex === 0}>
              <FiArrowLeft /> Previous
            </button>

            {isLastQuestionInSection ? (
              isLastSection ? (
                // Last question of the last section — nothing further to
                // advance to in-card; Submit Test lives in the bottom bar.
                <button className="q-nav-btn" disabled>
                  Next <FiArrowRight />
                </button>
              ) : (
                <button
                  className="q-nav-btn q-nav-btn--section"
                  onClick={goNextSection}
                >
                  Next Section <FiArrowRight />
                </button>
              )
            ) : (
              <button className="q-nav-btn" onClick={goNextQuestion}>
                Next <FiArrowRight />
              </button>
            )}
          </div>
        </div>

        <div className="palette-panel">
          <div className="palette-title">{currentSection.label}</div>
          <div className="palette-grid">
            {currentSection.questions.map((q, idx) => (
              <button
                key={q.id}
                className={`pal-btn ${getQuestionStatus(idx, q)}`}
                onClick={() => jumpToQuestion(idx)}
                title={`Question ${q.id}`}
              >
                {q.id}
              </button>
            ))}
          </div>
          <div className="palette-legend">
            <div><span className="legend-dot" style={{ background: "#16a34a" }} /> Current</div>
            <div><span className="legend-dot" style={{ background: "#dcfce7", border: "1px solid #16a34a" }} /> Answered ({paletteCounts.answered})</div>
            <div><span className="legend-dot" style={{ background: "#fee2e2", border: "1px solid #fca5a5" }} /> Not answered ({paletteCounts.notAnswered})</div>
            <div><span className="legend-dot" style={{ background: "#f9fafb", border: "1px solid #d1d5db" }} /> Not visited ({paletteCounts.notVisited})</div>
          </div>
        </div>
      </div>

      <div className="bottom-bar">
        <div className="bottom-bar-inner">
          <div className="dock-left">
            <button className="btn-exit" onClick={handleExitTest}>
              <FiLogOut /> Exit
            </button>
            <span style={{ fontSize: "0.83rem", color: "#6b7280" }}>
              {totalAnsweredCount} / {MAT_CONFIG.totalQuestions} answered
            </span>
          </div>
          <div className="dock-right">
            {activeSectionIndex > 0 && (
              <button className="btn-secondary" onClick={goPrevSection}>
                <FiArrowLeft /> Prev Section
              </button>
            )}
            {!isLastSection && (
              <button className="btn-secondary" onClick={goNextSection}>
                Next Section <FiArrowRight />
              </button>
            )}
            <button className="btn-primary" onClick={handleManualSubmit}>
              Submit Test <FiSave />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}